import { ChangeEvent, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  AlignLeft,
  FileText,
  Gauge,
  Hash,
  ImagePlus,
  Layers,
  Scale,
  Shapes,
  Tags as TagsIcon,
  Type,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DropZone } from '@/components/ui/drop-zone';
import SearchSelect from '@/components/ui/search-select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import {
  useCreateTagMutation,
  useGetTagsQuery,
} from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';
import { thumbnailExtensions } from '@/redux/post/post.schema';
import { useDeletePostDeleteMutation } from '@/redux/post/post.api.slice';
import {
  useGetProjectGroupByIdQuery,
  useSubmitProjectGroupMutation,
  useUpdateProjectGroupMutation,
  useUpdateProjectGroupProjectsMutation,
} from '@/redux/projectGroup/projectGroup.api.slice';
import { IUpdateProjectGroupRequest } from '@/redux/projectGroup/projectGroup.d';
import { useGetPreSignedUploadUrlMutation } from '@/redux/attachment/attachment.api.slice';
import {
  AttachmentParentType,
  AttachmentType,
  IAttachmentCategory,
} from '@/redux/attachment/attachment.d';
import hashFile from '@/lib/xxhash';
import Panel from '../components/Panel';
import FieldLabel, { Required } from '../components/FieldLabel';
import FieldError, { redlineControl } from '../components/FieldError';
import DifficultyPicker from '../components/DifficultyPicker';
import LicenseSelector from '../components/LicenseSelector';
import TagInput from '../components/TagInput';
import WizardFooter from '../components/WizardFooter';
import SinglePieceConfirm from '../components/SinglePieceConfirm';
import GroupReviewWorkpieceCard, {
  WorkpieceStatus,
} from '../components/GroupReviewWorkpieceCard';
import { STEP_HEADING } from '../components/fileCardStyles';
import { fmtSize, plural } from '../utils/fileHelpers';
import {
  extractErrorMessage,
  LICENSE_TO_CC_CODE,
  uploadFileToPresignedUrl,
} from '../utils/postSync';
import { restoreGroupCoverUrl } from '../utils/restorePost';
import { missingForGroup, MIN_GROUP_PROJECTS } from '../utils/validation';
import useFieldErrors from '../useFieldErrors';
import useConvertToSingle from '../useConvertToSingle';
import {
  addGroupTag,
  openWorkpiece,
  removeChildId,
  removeGroupTag,
  selectV2,
  setGroupCover,
  setGroupDetails,
  setGroupSubmitCounts,
  setMultiStage,
  setSubmitted,
  setSubmitting,
  setWorkpieceIndex,
  startNextWorkpiece,
} from '../v2Upload.slice';

const HINT = 'text-xs text-muted-foreground mt-1.5';

const LICENCE_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(LICENSE_TO_CC_CODE).map(([licence, code]) => [code, licence])
);

function TooFewNotice({
  onAddAnother,
  onSubmitAsSingle,
}: {
  onAddAnother: () => void;
  onSubmitAsSingle?: () => void;
}) {
  return (
    <div className="rounded-xl border border-warning/50 bg-warning/[0.07] p-3.5">
      <p className="text-[13px] font-semibold text-heading">
        A project needs at least {MIN_GROUP_PROJECTS} workpieces
      </p>
      <p className={cn(HINT, 'mt-1')}>
        Add another workpiece, or submit this one on its own as a single
        project.
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" onClick={onAddAnother}>
          Add another workpiece
        </Button>
        {onSubmitAsSingle && (
          <Button size="sm" variant="outline" onClick={onSubmitAsSingle}>
            Submit as a single project
          </Button>
        )}
      </div>
    </div>
  );
}

// fallow-ignore-next-line complexity
export default function GroupReviewStep() {
  const dispatch = useDispatch();
  const { groupDetails, groupCover, groupId, submitting } =
    useSelector(selectV2);

  const { data: group } = useGetProjectGroupByIdQuery(
    { id: groupId ?? '' },
    { skip: !groupId }
  );
  const children: Array<{ _id: string }> = group?.projects ?? [];
  const childIds = children.map((child) => child._id);

  const [statuses, setStatuses] = useState<Record<string, WorkpieceStatus>>({});
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const { data: categories } = useGetCategoriesQuery({
    type: ICategoryType.POST,
  });
  const { data: tags } = useGetTagsQuery({ type: ITagType.POST });
  const [createTag] = useCreateTagMutation();
  const [updateProjectGroup] = useUpdateProjectGroupMutation();
  const [updateProjectGroupProjects] = useUpdateProjectGroupProjectsMutation();
  const [deletePost] = useDeletePostDeleteMutation();
  const [submitProjectGroup] = useSubmitProjectGroupMutation();
  const convertToSingle = useConvertToSingle();
  const [confirming, setConfirming] = useState(false);
  const [getPreSignedUploadUrl] = useGetPreSignedUploadUrlMutation();

  const missing = missingForGroup({ ...groupDetails, material: '' }, childIds);
  const { bad, props, guard } = useFieldErrors(missing);

  const notReady = childIds.filter((id) => statuses[id] !== 'ready');

  // Counted off the surviving children, not incremented: a removal would
  // otherwise leave the next workpiece numbered past the set's size.
  const addAnotherWorkpiece = () => {
    dispatch(startNextWorkpiece());
    dispatch(setWorkpieceIndex(childIds.length + 1));
  };

  const reportStatus = useCallback((id: string, status: WorkpieceStatus) => {
    setStatuses((prev) =>
      prev[id] === status ? prev : { ...prev, [id]: status }
    );
  }, []);

  const dropCover = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!thumbnailExtensions.includes(ext)) {
      toast.error('Invalid file type', {
        description: `A cover has to be ${thumbnailExtensions.join(', ')}`,
      });
      input.value = '';
      return;
    }
    dispatch(setGroupCover({ file, preview: URL.createObjectURL(file) }));
  };

  /** The wizard holds category and tag NAMES; the group API wants ids. */
  const resolveCategoryId = (): string | undefined =>
    (categories ?? []).find(
      (c) =>
        c.name.trim().toLowerCase() ===
        groupDetails.category.trim().toLowerCase()
    )?._id;

  // fallow-ignore-next-line complexity
  const resolveTagIds = async (): Promise<string[]> =>
    Promise.all(
      // fallow-ignore-next-line complexity
      groupDetails.tags.map(async (name) => {
        const existing = tags?.find(
          (t) => t.name.trim().toLowerCase() === name.trim().toLowerCase()
        );
        if (existing?._id) return existing._id as string;
        const created = await createTag({ name, type: ITagType.POST }).unwrap();
        if (!created?._id) throw new Error(`Could not create tag "${name}"`);
        return created._id as string;
      })
    );

  // fallow-ignore-next-line complexity
  const buildGroupPatch = (tagIds: string[]): IUpdateProjectGroupRequest => {
    const categoryId = resolveCategoryId();
    const licenceType = LICENCE_TYPE[groupDetails.license];
    return {
      name: groupDetails.title,
      description: groupDetails.description,
      tags: tagIds,
      // Clearing a restored cover has to reach the server; a new one is
      // attached by uploadCover instead.
      ...(restoreGroupCoverUrl(group) && !groupCover
        ? { thumbnail: null }
        : {}),
      ...(categoryId ? { category: categoryId } : {}),
      ...(groupDetails.difficulty
        ? { difficulty: groupDetails.difficulty }
        : {}),
      ...(licenceType ? { licenceType } : {}),
    };
  };

  // fallow-ignore-next-line complexity
  const uploadCover = async (id: string) => {
    const file = groupCover?.file;
    if (!file) return;
    const fileHash = await hashFile(file);
    const [signed] = await getPreSignedUploadUrl({
      attachments: [
        {
          parentId: id,
          parentType: AttachmentParentType.PROJECT_GROUP,
          category: IAttachmentCategory.projectGroupThumbnail,
          fileName: file.name,
          fileHash: fileHash.toString(),
          fileSize: file.size,
          resourceType: AttachmentType.image,
        },
      ],
    }).unwrap();
    // No signed URL means the hash already exists server-side; the attachment id
    // still comes back and still has to be attached.
    if (signed?.signedUrl)
      await uploadFileToPresignedUrl(file, signed.signedUrl);
    if (signed?.attachmentId) {
      await updateProjectGroup({
        id,
        data: { thumbnail: signed.attachmentId.toString() },
      }).unwrap();
    }
  };

  const handleSubmit = async () => {
    if (!groupId || !guard()) return;
    dispatch(setSubmitting(true));
    try {
      const tagIds = await resolveTagIds();
      await updateProjectGroup({
        id: groupId,
        data: buildGroupPatch(tagIds),
      }).unwrap();
      await uploadCover(groupId);
      const result = await submitProjectGroup(groupId).unwrap();
      dispatch(
        setGroupSubmitCounts({
          workpieces: result.workpieceCount,
          files: result.fileCount,
        })
      );
      dispatch(setSubmitted(true));
    } catch (err) {
      toast.error('Submission failed', {
        description: extractErrorMessage(
          err,
          'Something went wrong while submitting. Please try again.'
        ),
      });
      dispatch(setSubmitting(false));
    }
  };

  const confirmRemoval = async () => {
    const id = pendingRemoval;
    if (!id || !groupId) return;
    setRemoving(true);
    try {
      // Detach before deleting, or the group keeps pointing at a dead post.
      await updateProjectGroupProjects({
        id: groupId,
        data: { projects: childIds.filter((childId) => childId !== id) },
      }).unwrap();
      await deletePost(id).unwrap();
      dispatch(removeChildId(id));
      setPendingRemoval(null);
    } catch (err) {
      toast.error('Could not remove that workpiece', {
        description: extractErrorMessage(err, 'Please try again.'),
      });
    } finally {
      setRemoving(false);
    }
  };

  const tooFew = childIds.length < MIN_GROUP_PROJECTS;
  const lonePiece = childIds.length === 1;

  const footerHint = () => {
    if (tooFew) {
      return `A project needs at least ${MIN_GROUP_PROJECTS} workpieces`;
    }
    if (notReady.length > 0) {
      return `${notReady.length} ${plural(notReady.length, 'workpiece', 'workpieces')} still ${plural(notReady.length, 'needs', 'need')} work`;
    }
    if (missing.length > 0) {
      return `Still needs ${missing.map((m) => m.label).join(', ')}`;
    }
    return 'Reviewed by a moderator before it goes live';
  };

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>Review your project & submit</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-[640px]">
        Name the whole project, check every workpiece is ready, then send the
        set for review as one submission.
      </p>

      <div className="max-w-[1400px] space-y-5">
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(340px,400px)]">
          <div className="space-y-5">
            <Panel
              icon={FileText}
              title="Project Basics"
              help="The name and blurb a maker reads first. Say what the whole project makes — 'Walnut desk set' beats 'my parts'."
            >
              <div {...props('title')}>
                <FieldLabel icon={Type}>
                  Project Name
                  <Required />
                </FieldLabel>
                <Input
                  value={groupDetails.title}
                  onChange={(e) =>
                    dispatch(setGroupDetails({ title: e.target.value }))
                  }
                  placeholder="e.g. Walnut desk set"
                  className={redlineControl(bad('title'))}
                />
                <FieldError
                  missing={missing}
                  field="title"
                  show={bad('title')}
                />
              </div>

              <div {...props('description')}>
                <FieldLabel icon={AlignLeft}>
                  Short Description
                  <Required />
                </FieldLabel>
                <Textarea
                  value={groupDetails.description}
                  onChange={(e) =>
                    dispatch(setGroupDetails({ description: e.target.value }))
                  }
                  placeholder="What the set makes, and how the pieces fit together…"
                  rows={3}
                  className={redlineControl(bad('description'))}
                />
                <FieldError
                  missing={missing}
                  field="description"
                  show={bad('description')}
                />
              </div>
            </Panel>

            <Panel
              icon={TagsIcon}
              title="Project Info"
              help="How people find this project: the category decides where it turns up when they browse, and tags are what search matches on."
              className="overflow-visible"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div {...props('category')}>
                  <FieldLabel icon={Shapes}>
                    Category
                    <Required />
                  </FieldLabel>
                  <SearchSelect
                    aria-label="Category"
                    value={groupDetails.category}
                    options={(categories ?? []).map((c) => c.name)}
                    onChange={(v) => dispatch(setGroupDetails({ category: v }))}
                    placeholder="Select a category"
                    invalid={bad('category')}
                  />
                  <FieldError
                    missing={missing}
                    field="category"
                    show={bad('category')}
                  />
                </div>

                <div {...props('difficulty')}>
                  <FieldLabel icon={Gauge}>
                    Difficulty
                    <Required />
                  </FieldLabel>
                  <DifficultyPicker
                    label="Project Difficulty"
                    value={groupDetails.difficulty}
                    onChange={(v) =>
                      dispatch(setGroupDetails({ difficulty: v }))
                    }
                    invalid={bad('difficulty')}
                  />
                  <FieldError
                    missing={missing}
                    field="difficulty"
                    show={bad('difficulty')}
                  />
                </div>
              </div>

              <div {...props('tags')}>
                <FieldLabel icon={Hash}>Tags</FieldLabel>
                <TagInput
                  tags={groupDetails.tags}
                  onAdd={(t) => dispatch(addGroupTag(t))}
                  onRemove={(i) => dispatch(removeGroupTag(i))}
                  invalid={bad('tags')}
                />
                <FieldError missing={missing} field="tags" show={bad('tags')} />
              </div>
            </Panel>

            <Panel
              icon={ImagePlus}
              title="Cover Image"
              help="The one image the project card leads with. Without it the card falls back to the first workpiece's photo."
            >
              <div data-focus="cover">
                {groupCover ? (
                  <div className="flex items-center gap-3 rounded-lg border p-2">
                    <img
                      src={groupCover.preview}
                      alt="Cover"
                      className="h-14 w-20 shrink-0 rounded-md border bg-muted object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium">
                        {groupCover.file?.name ?? 'Current cover'}
                      </p>
                      <p className={cn(HINT, 'mt-0.5')}>
                        {groupCover.file
                          ? `${fmtSize(groupCover.file.size)} — see it on the card in the preview panel.`
                          : 'Drop a new image to replace it.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => dispatch(setGroupCover(null))}
                      aria-label="Remove cover image"
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <DropZone onDrop={dropCover} className="border-input py-10">
                    <span className="pointer-events-none flex flex-col items-center gap-2">
                      <ImagePlus className="h-7 w-7 text-muted-foreground/50" />
                      <span className="text-[13px] font-medium">
                        Drop a cover image, or click to choose
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {thumbnailExtensions.join(', ')} — optional
                      </span>
                    </span>
                  </DropZone>
                )}
              </div>
            </Panel>
          </div>

          <Panel
            icon={Layers}
            title="Workpieces"
            help="Every piece you authored in this session. Each one has to pass its own review before the set can be submitted."
            actions={
              childIds.length > 0 ? (
                <Badge variant="secondary">
                  {childIds.length - notReady.length} of {childIds.length} ready
                </Badge>
              ) : undefined
            }
          >
            <div {...props('projects')} className="space-y-3">
              {tooFew && (
                <TooFewNotice
                  onAddAnother={addAnotherWorkpiece}
                  onSubmitAsSingle={
                    childIds.length === 1
                      ? () => {
                          convertToSingle(childIds[0]).catch(() => {});
                        }
                      : undefined
                  }
                />
              )}
              {childIds.map((id, index) => (
                <GroupReviewWorkpieceCard
                  key={id}
                  postId={id}
                  index={index}
                  onStatus={reportStatus}
                  onEdit={() =>
                    dispatch(openWorkpiece({ postId: id, index: index + 1 }))
                  }
                  onRemove={setPendingRemoval}
                  busy={removing}
                />
              ))}
              <FieldError
                missing={missing}
                field="projects"
                show={bad('projects')}
              />
            </div>
          </Panel>
        </div>

        <Panel
          icon={Scale}
          title="License"
          help="One license for the whole project — the submit stamps it onto every workpiece."
        >
          <div data-focus="license-presets">
            <LicenseSelector
              selected={groupDetails.license}
              onSelect={(code) => dispatch(setGroupDetails({ license: code }))}
            />
          </div>
        </Panel>
      </div>

      <AlertDialog
        open={Boolean(pendingRemoval)}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this workpiece?</AlertDialogTitle>
            <AlertDialogDescription>
              Its draft and every file you uploaded for it are deleted for good.
              The rest of the project is untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              disabled={removing}
              onClick={(e) => {
                e.preventDefault();
                confirmRemoval();
              }}
            >
              Delete workpiece
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SinglePieceConfirm
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={() => {
          convertToSingle(childIds[0]).catch(() => {});
        }}
      />

      <WizardFooter
        onBack={() => dispatch(setMultiStage('interstitial'))}
        onContinue={lonePiece ? () => setConfirming(true) : handleSubmit}
        busy={submitting}
        busyLabel="Submitting…"
        backDisabled={submitting}
        continueDisabled={childIds.length === 0 || notReady.length > 0}
        continueLabel="Submit for Review"
        hint={
          <span className="text-xs text-muted-foreground font-tertiary">
            {footerHint()}
          </span>
        }
      />
    </div>
  );
}
